import React, { useMemo } from 'react';
import { ButtonV2 } from '@withjoy/joykit';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { AccommodationOfferingCard } from './AccommodationOfferingCard';
import { useTranslation } from '@shared/core';
import { useAccommodationSearchPageRedirectUrl } from './useAccommodationSearchPageRedirectUrl';
import { AccommodationsPushStepProps } from './AccommodationsPushStep.types';
import { useThanksForRSVPTelemetry } from '@apps/thanksForRsvp/ThanksForRSVP.telemetry';
import { ValuePropsSection } from './ValuePropsSection';
import { DiscountCode, Refund } from '@withjoy/joykit/icons';
import { Content } from './Content';
import { useHotelRoutePaths } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';

const HOTEL_IMAGE_URL = 'https://withjoy.com/media/print/marriot-bonvoy-hotel-preview.jpg';

interface NoAccommodationsOrTravelCohortViewProps extends AccommodationsPushStepProps {}

export const NoAccommodationsOrTravelCohortView = (props: NoAccommodationsOrTravelCohortViewProps) => {
  const { params: queryParams } = useAccommodationSearchPageRedirectUrl(props);
  const { accommodationPushStepButtonClick } = useThanksForRSVPTelemetry();

  const hotelRoute = useHotelRoutePaths();

  const hotelUrl = hotelRoute.hotelSearch.buildRelativeUrl(props.eventHandle, {
    utm_medium: 'web',
    utm_source: 'confirmation_rsvp',
    utm_campaign: 'hasNoAccommodationsAndTravelPages'
  });

  const handleOnBrowseHotelsClick = useEventCallback(() => {
    accommodationPushStepButtonClick({ cta: 'exploreAllHotels', cohort: 'hasNoAccommodationsAndTravelPages', queryParams });
  });
  const { t } = useTranslation('thanksForRSVP');

  const transAccommodationsStep = useMemo(() => {
    return t('accommodationsPushStep');
  }, [t]);

  const valueProps = useMemo(() => {
    const transValueProps = t('accommodationsPushStep', 'valueProps');
    return [
      {
        title: transValueProps.lowestRate.title(),
        description: transValueProps.lowestRate.description(),
        icon: () => <DiscountCode size={24} />
      },
      {
        title: transValueProps.refundable.title(),
        description: transValueProps.refundable.description(),
        icon: () => <Refund size={24} />
      }
    ];
  }, [t]);

  return (
    <Content title={transAccommodationsStep.noAccomOrTravelCohort.title()} message={transAccommodationsStep.message()}>
      <ValuePropsSection items={valueProps} />
      <AccommodationOfferingCard marginTop={6}>
        <AccommodationOfferingCard.AssetContainer>
          <AccommodationOfferingCard.Asset imageUrl={HOTEL_IMAGE_URL} />
        </AccommodationOfferingCard.AssetContainer>

        <AccommodationOfferingCard.TitleSection marginTop={5} title={transAccommodationsStep.genericOfferingCard.title()} isPreferredOffering />

        <AccommodationOfferingCard.RateSection
          marginTop={5}
          rateInMinorUnits={0}
          rateDisplayOverride={transAccommodationsStep.noAccomOrTravelCohort.rateDisplayOverride()}
          rateType="daily"
        />

        <ButtonV2 as="a" href={hotelUrl} variant="solid" intent="neutral" fullWidth shape="rounded" marginTop={7} onClick={handleOnBrowseHotelsClick}>
          {transAccommodationsStep.genericOfferingCard.browseHotelsCta()}
        </ButtonV2>
      </AccommodationOfferingCard>
    </Content>
  );
};
